import { Link, useQuery, useTranslator } from "gemi/client";
import { useState } from "react";
import { trackEvent } from "../utils/analytics";
import { font, SwooshWord } from "./_shared";

type Billing = "monthly" | "yearly";

// `lookup` matches the Stripe lookup-key prefix served by /public/prices
// (`basic_*`, `standard_*`, `premium_*`); `null` is the Enterprise plan (no self-serve price, a
// "contact us" card). The `premium` tier is shown as "Max". (T-0611)
const PLANS = [
  { name: "Solo", featKey: "feat_solo", lookup: "basic", featured: false },
  { name: "Pro", featKey: "feat_pro", lookup: "standard", featured: true },
  { name: "Max", featKey: "feat_premium", lookup: "premium", featured: false },
  {
    name: "Enterprise",
    featKey: "feat_enterprise",
    lookup: null,
    featured: false,
  },
] as const;

const GRADIENT =
  "linear-gradient(168deg, #F0B04D 16%, #D9D9D9 51%, #98E3F9 74%)";

const formatUsd = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);

function Check({ dark }: { dark: boolean }) {
  return (
    <span
      className="flex flex-none items-center justify-center rounded-full p-1"
      style={{ background: dark ? "#343339" : "#ffffff" }}
    >
      <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="m5 12.5 4.5 4.5L19 7"
          stroke={dark ? "#ffffff" : "#343339"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export default function Pricing() {
  const t = useTranslator("PricingPageV2");
  const { data: prices } = useQuery("/public/prices");
  const [billing, setBilling] = useState<Billing>("yearly");

  // Per-month price + Stripe lookup-key for a plan, from the live /public/prices
  // feed. Yearly rows carry the annual total, so divide by 12 for the display.
  const priceFor = (lookup: string) => {
    // `key` is Stripe's nullable `lookup_key`; the predicate narrows the match to a row that has one.
    const row = prices.data.find(
      (p): p is typeof p & { key: string } =>
        p.key?.startsWith(`${lookup}_${billing}`) === true,
    );
    if (!row) return null;
    const perMonth = billing === "yearly" ? row.price / 12 : row.price;
    return { priceCode: row.key, display: formatUsd(perMonth) };
  };

  const seg = (active: boolean) => ({
    background: active ? GRADIENT : "transparent",
    color: active ? "#343339" : "#ffffff",
  });

  return (
    <section className="relative px-5 pb-16 pt-16 lg:px-9 lg:pb-[120px] lg:pt-14">
      <div className="mx-auto flex max-w-[1368px] flex-col gap-12">
        <div className="flex flex-col items-start gap-12 lg:items-center">
          <h1
            className="m-0 text-[32px] font-medium leading-[1.05] tracking-[-1.28px] text-[#343339] sm:text-[40px] sm:tracking-[-1.6px] lg:text-center lg:text-[56px] lg:tracking-[-2.24px]"
            style={font.display}
          >
            {t("title_a")}
            <SwooshWord id="priceSwoosh">
              <span className="first-letter:text-[1.16em]">
                {t("title_free")}
              </span>
            </SwooshWord>
            {t("title_b")}
          </h1>
          <div
            className="flex items-center gap-2 rounded-full bg-[#343339] p-1.5"
            style={font.body}
          >
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              className="rounded-full px-4 py-1.5 text-[14px] font-medium"
              style={seg(billing === "monthly")}
            >
              {t("monthly")}
            </button>
            <button
              type="button"
              onClick={() => setBilling("yearly")}
              className="rounded-full px-4 py-1.5 text-[14px] font-medium"
              style={seg(billing === "yearly")}
            >
              {t("yearly")}
            </button>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[312px] flex-col gap-4 sm:max-w-none sm:grid sm:grid-cols-2 sm:items-stretch sm:gap-6 lg:grid-cols-4 lg:gap-10">
          {PLANS.map((plan) => {
            const featured = plan.featured;
            const isEnterprise = plan.lookup === null;
            const priceInfo = plan.lookup ? priceFor(plan.lookup) : null;
            // A paid tier whose lookup key is missing from /public/prices
            // (inactive/misconfigured in Stripe) has no price to show — drop the
            // card rather than render a $0 plan. Enterprise has no price by design.
            if (!isEnterprise && !priceInfo) return null;
            const features: string[] = t(plan.featKey).split(" | ");
            const ink = featured ? "#ffffff" : "#343339";
            const ctaClass =
              "flex h-[50px] items-center justify-center rounded-[30px] px-8 text-[14px] font-medium no-underline lg:h-auto lg:py-5";
            const ctaStyle = featured
              ? { background: "#ffffff", color: "#343339" }
              : { background: "#343339", color: "#ffffff" };
            return (
              <div
                key={plan.name}
                className="relative flex flex-col justify-between gap-12 overflow-hidden rounded-[20px] border border-[#f0b04d] p-8 lg:rounded-[30px]"
                style={{ background: featured ? "#343339" : "#ffffff" }}
              >
                {featured && (
                  <div
                    className="absolute right-[-44px] top-7 rotate-45 px-12 py-1 text-[14px] tracking-[-0.14px] text-[#343339]"
                    style={{ ...font.body, background: GRADIENT }}
                  >
                    {t("popular")}
                  </div>
                )}
                <div className="flex flex-col gap-12">
                  <h3
                    className="m-0 text-[20px] font-medium leading-7 tracking-[-0.4px]"
                    style={{ ...font.display, color: ink }}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {features.map((f) => (
                      <div key={f} className="flex items-center gap-4">
                        <Check dark={!featured} />
                        <span
                          className="text-[16px] leading-6 tracking-[-0.16px]"
                          style={{ color: ink }}
                        >
                          {f}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex items-end" style={{ color: ink }}>
                    <span
                      className="text-[66px] font-normal leading-none tracking-[-3.3px]"
                      style={font.display}
                    >
                      {priceInfo ? priceInfo.display : t("enterprise_price")}
                    </span>
                    {priceInfo && (
                      <span className="mb-2 ml-1 text-[14px] tracking-[-0.14px] opacity-60">
                        {t("per_month")}
                      </span>
                    )}
                  </div>
                  {/* Only Enterprise gets here without a price; the guard above drops any other. */}
                  {!priceInfo ? (
                    <Link
                      href="/contact"
                      onClick={() =>
                        trackEvent({
                          event: "pricing_tier_click",
                          params: { tier: "enterprise" },
                        })
                      }
                      className={ctaClass}
                      style={ctaStyle}
                    >
                      {t("contact_us")}
                    </Link>
                  ) : (
                    <Link
                      href="/checkout/:priceCode"
                      params={{ priceCode: priceInfo.priceCode }}
                      onClick={() =>
                        trackEvent({
                          event: "pricing_tier_click",
                          params: { tier: priceInfo.priceCode },
                        })
                      }
                      className={ctaClass}
                      style={ctaStyle}
                    >
                      {t("subscribe_to", { plan: plan.name })}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-start gap-1.5 lg:items-center lg:justify-center">
          <img
            src="/v2/icons/star.svg"
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className="mt-px size-5 lg:mt-0"
          />
          <span className="text-[14px] tracking-[0.14px] text-[#343339] opacity-60">
            {t("footnote")}
          </span>
        </div>
      </div>
    </section>
  );
}
